// Shared email-OTP 2FA step used by all login pages. Handles code entry,
// resend cooldown, recovery-code fallback, and one-time backup code display.
import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, MailCheck, ShieldCheck, Copy, Download, AlertTriangle } from "lucide-react";
import { useTranslation } from "react-i18next";

export default function TwoFactorStep({ challenge, onVerified, onDone, onBack }) {
  const { t } = useTranslation();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const [recCodes, setRecCodes] = useState(null);
  const [verifiedUser, setVerifiedUser] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const verify = async (e) => {
    e?.preventDefault?.();
    if (!code.trim()) return;
    setBusy(true); setError("");
    const res = await onVerified(challenge.challenge_id, code.trim());
    setBusy(false);
    if (!res.ok) { setError(res.error || t("twofa.incorrect")); return; }
    if (res.recoveryCodes?.length) {
      setRecCodes(res.recoveryCodes);
      setVerifiedUser(res.user);
    } else {
      onDone(res.user);
    }
  };

  const resend = async () => {
    try {
      const { data } = await api.post("/auth/2fa/resend", { challenge_id: challenge.challenge_id });
      setCooldown(30);
      if (data.delivery === "sent") toast.success(t("twofa.resent"));
      else toast.error(t("twofa.resend_failed"));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const copyText = (list) => {
    navigator.clipboard?.writeText(list.join("\n"));
    toast.success(t("twofa.codes_copied"));
  };
  const downloadText = (list) => {
    const blob = new Blob([`Legacy Detailing IO — 2FA backup codes\n\n${list.join("\n")}\n\nEach code works once. Keep them somewhere safe.`], { type: "text/plain" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "legacy-detailing-backup-codes.txt";
    a.click();
    URL.revokeObjectURL(a.href);
  };
  const copyCodes = () => copyText(recCodes);
  const downloadCodes = () => downloadText(recCodes);

  if (recCodes) {
    return (
      <div data-testid="twofa-recovery-codes">
        <div className="flex items-center gap-2 mb-2">
          <ShieldCheck size={18} className="text-emerald-400" />
          <h2 className="font-heading text-xl font-bold">{t("twofa.save_codes")}</h2>
        </div>
        <p className="text-sm text-slate-400 mb-4">
          {t("twofa.codes_body_pre")} <b className="text-white">{t("twofa.only_once")}</b>.
        </p>
        <div className="grid grid-cols-2 gap-1.5 font-mono text-sm bg-white/[0.04] border border-white/10 rounded-md p-4 mb-4">
          {recCodes.map((c) => <div key={c} className="tabular-nums">{c}</div>)}
        </div>
        <div className="flex gap-2 mb-4">
          <button onClick={copyCodes} className="ld-btn text-xs flex-1 justify-center" data-testid="twofa-copy-codes">
            <Copy size={12} /> {t("twofa.copy")}
          </button>
          <button onClick={downloadCodes} className="ld-btn text-xs flex-1 justify-center" data-testid="twofa-download-codes">
            <Download size={12} /> {t("twofa.download")}
          </button>
        </div>
        <button onClick={() => onDone(verifiedUser)} className="ld-btn ld-btn-primary w-full justify-center"
          data-testid="twofa-codes-continue">
          {t("twofa.saved_continue")}
        </button>
      </div>
    );
  }

  return (
    <div data-testid="twofa-step">
      <div className="flex items-center gap-2 mb-2">
        <MailCheck size={18} className="text-[var(--ld-royal-hover)]" />
        <h2 className="font-heading text-xl font-bold">{t("twofa.check_email")}</h2>
      </div>
      <p className="text-sm text-slate-400">
        {t("twofa.sent_code_pre")} <b className="text-white">{challenge.email_hint}</b>.{" "}
        {t("twofa.sent_code_post", { min: challenge.expires_in_min || 10 })}
      </p>
      {challenge.setup_codes?.length ? (
        <div className="mt-3" data-testid="twofa-setup-codes">
          <div className="rounded-md bg-amber-500/10 border border-amber-500/30 text-amber-200 text-xs px-3 py-2 flex items-start gap-2">
            <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
            <span>{t("twofa.setup_codes_msg_pre")} <b>{t("twofa.one_time_backup")}</b> {t("twofa.setup_codes_msg_post")}</span>
          </div>
          <div className="grid grid-cols-2 gap-1.5 font-mono text-sm bg-white/[0.04] border border-white/10 rounded-md p-4 mt-2">
            {challenge.setup_codes.map((c) => <div key={c} className="tabular-nums">{c}</div>)}
          </div>
          <div className="flex gap-2 mt-2">
            <button onClick={() => copyText(challenge.setup_codes)} className="ld-btn text-xs flex-1 justify-center" data-testid="twofa-setup-copy">
              <Copy size={12} /> {t("twofa.copy")}
            </button>
            <button onClick={() => downloadText(challenge.setup_codes)} className="ld-btn text-xs flex-1 justify-center" data-testid="twofa-setup-download">
              <Download size={12} /> {t("twofa.download")}
            </button>
          </div>
        </div>
      ) : challenge.delivery === "failed" && (
        <div className="mt-3 rounded-md bg-amber-500/10 border border-amber-500/30 text-amber-200 text-xs px-3 py-2 flex items-start gap-2"
          data-testid="twofa-delivery-failed">
          <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
          <span>{t("twofa.delivery_failed_pre")} <b>{t("twofa.backup_recovery")}</b> {t("twofa.delivery_failed_post")}</span>
        </div>
      )}
      <form onSubmit={verify} className="mt-5 space-y-4">
        {error && (
          <div className="bg-red-500/10 border border-red-500/40 text-red-300 text-sm rounded-md px-4 py-3" data-testid="twofa-error">
            {error}
          </div>
        )}
        <input
          ref={inputRef} value={code} onChange={(e) => setCode(e.target.value)}
          placeholder={t("twofa.code_ph")}
          autoComplete="one-time-code" inputMode="text" maxLength={12}
          className="w-full bg-[var(--ld-surface-2)] border border-white/10 rounded-md px-4 py-3 text-center text-lg tracking-[0.3em] font-mono focus:outline-none focus:border-[var(--ld-royal)] focus:ring-2 focus:ring-[var(--ld-royal-glow)]"
          data-testid="twofa-code-input"
        />
        <button type="submit" disabled={busy || !code.trim()}
          className="ld-btn ld-btn-primary w-full justify-center" data-testid="twofa-verify-btn">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />}
          {busy ? t("twofa.verifying") : t("twofa.verify_btn")}
        </button>
      </form>
      <div className="mt-4 flex items-center justify-between text-xs">
        <button onClick={onBack} className="text-slate-500 hover:text-white" data-testid="twofa-back-btn">
          {t("twofa.back")}
        </button>
        <button onClick={resend} disabled={cooldown > 0}
          className="text-[var(--ld-royal-hover)] hover:underline disabled:opacity-50 disabled:no-underline"
          data-testid="twofa-resend-btn">
          {cooldown > 0 ? t("twofa.resend_cooldown", { s: cooldown }) : t("twofa.resend")}
        </button>
      </div>
    </div>
  );
}
